/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useMemo, useState} from 'react'
import {Company} from 'types/entities/Company'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import {useTranslation} from 'react-i18next'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {useAppDispatch} from 'helpers/store'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {closeModal} from 'helpers/actions'
import {toast} from 'utils/toastUtils'
import {DocumentDuplicateIcon} from '@heroicons/react/24/outline'
import MergeCompaniesSearchTagsList from './MergeCompaniesModalComponents/MergeCompaniesSearchTagsList'
import MergeCompaniesCardsList from './MergeCompaniesModalComponents/MergeCompaniesCardsList'
import MergeAuditLogPanel from './MergeCompaniesModalComponents/MergeAuditLogPanel'
import { appendMergeAuditLogEntry } from 'store/crm/CrmReducer'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import { collectDuplicateContactEmails } from 'utils/contacts/contactDuplicateUtils'

const isMergeableCompany = (c: Company) =>
    c.type === CompanyBranchType.Parent ||
    c.type === CompanyBranchType.Branch ||
    c.type === CompanyBranchType.Company

interface MergeCompaniesModalProps {
    duplicates: Company[]
    currentCompany?: Company | null
    initialSearchTerm?: string
    onSuccess?: () => void
}

const MergeCompaniesModal: React.FC<MergeCompaniesModalProps> = ({
    duplicates,
    initialSearchTerm = '',
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const [selectedMainId, setSelectedMainId] = useState<number | null>(null)
    const [selectedForMergeIds, setSelectedForMergeIds] = useState<Set<number>>(
        new Set()
    )
    const [isMerging, setIsMerging] = useState(false)
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
    const [allCompanies, setAllCompanies] = useState<Company[]>(duplicates.filter(isMergeableCompany))
    const [fullDetailsMap, setFullDetailsMap] = useState<
        Record<number, Company>
    >({})
    const [loadingDetails, setLoadingDetails] = useState(false)
    const [isSearching, setIsSearching] = useState(false)
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>(
        {}
    )
    const [searchTags, setSearchTags] = useState<
        { term: string; results: Company[] }[]
    >([])

    const duplicateContactEmails = useMemo(() => {
        const contacts = allCompanies.flatMap((company) => {
            const detail = fullDetailsMap[company.id as number] || company
            return detail.contacts || []
        })
        return collectDuplicateContactEmails(contacts)
    }, [allCompanies, fullDetailsMap])

    const toggleRow = (id: number) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const toggleMergeSelection = (id: number) => {
        setSelectedForMergeIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
                if (selectedMainId === id) setSelectedMainId(null)
            } else {
                next.add(id)
            }
            return next
        })
    }

    useEffect(() => {
        // Fetch full details for all duplicates to show contacts, addresses, etc.
        const fetchDetails = async () => {
            setLoadingDetails(true)
            const map: Record<number, Company> = { ...fullDetailsMap }

            try {
                // const toFetch = allCompanies.filter(
                //     (c) => c.id && !fullDetailsMap[c.id as number]
                // )
                // await Promise.all(
                //     toFetch.map(async (dup) => {
                //         if (dup.id) {
                //             try {
                //                 const detail = await dispatch(
                //                     CompaniesAction.fetchCompanyById(dup.id)
                //                 )
                //                 map[dup.id] = detail
                //             } catch (e) {
                //                 console.error(
                //                     `Failed to fetch details for company ${dup.id}`,
                //                     e
                //                 )
                //                 map[dup.id] = dup // Fallback to basic info
                //             }
                //         }
                //     })
                // )
                setFullDetailsMap(map)
            } finally {
                setLoadingDetails(false)
            }
        }

        if (allCompanies.length > 0) {
            fetchDetails()
        }
    }, [allCompanies, dispatch])

    const handleSearch = async () => {
        if (!searchTerm || searchTerm.length < 3) {
            toast.info(t('Search term must be at least 3 characters'))
            return
        }

        setIsSearching(true)
        try {
            const results = await dispatch(
                CompaniesAction.fetchCheckDuplicates({
                    name: searchTerm,
                })
            )

            // Add to search tags and update all companies
            const filteredResults = results.filter(isMergeableCompany)

            setSearchTags((prev) => {
                const isDuplicate = prev.some((t) => t.term === searchTerm)
                if (isDuplicate) return prev
                return [{ term: searchTerm, results: filteredResults }, ...prev]
            })

            setAllCompanies((prev) => {
                const combined = [...prev]
                const newResults = filteredResults.filter(
                    (res: Company) => !combined.find((c) => c.id === res.id)
                )
                return [...newResults, ...combined]
            })

            if (filteredResults.length === 0) {
                toast.info(t('No additional duplicates found'))
            }
            setSearchTerm('')
        } catch {
            toast.error(t('Failed to search for duplicates'))
        } finally {
            setIsSearching(false)
        }
    }
    const handleReset = () => {
        setSearchTerm('')
        setAllCompanies(duplicates.filter(isMergeableCompany))
        setSearchTags([])
        setSelectedForMergeIds(new Set())
        setSelectedMainId(null)
    }

    const handleRemoveTag = (termToRemove: string) => {
        setSearchTags((prev) => {
            const nextTags = prev.filter((t) => t.term !== termToRemove)

            // Recompute allCompanies from remaining tags + initial duplicates (maintaining order)
            let combined = [...duplicates.filter(isMergeableCompany)]
            // searchTags is [Newer, ..., Older]. We reverse to unshift Older then Newer.
            const reversedTags = [...nextTags].reverse()
            reversedTags.forEach((tag) => {
                const newOnly = tag.results.filter(
                    (res) => !combined.find((c) => c.id === res.id)
                )
                combined = [...newOnly, ...combined]
            })
            setAllCompanies(combined)

            return nextTags
        })
    }

    const handleMerge = async () => {
        if (!selectedMainId) {
            toast.warning(t('Select a main company before merging'))
            return
        }

        const companyIdsToMerge = Array.from(selectedForMergeIds)
            .map((id) => Number(id))
            .filter(
                (id) =>
                    Number.isFinite(id) &&
                    id > 0 &&
                    id !== Number(selectedMainId)
            )

        if (companyIdsToMerge.length === 0) {
            toast.warning(
                t('Select at least one additional company to merge into the main record')
            )
            return
        }

        setIsMerging(true)
        try {
            const mergeResult = await dispatch(
                CompaniesAction.fetchMergeCompanies({
                    main_company_id: Number(selectedMainId),
                    merged_company_ids: companyIdsToMerge,
                })
            )

            const mainCompany =
                fullDetailsMap[selectedMainId] ||
                allCompanies.find((company) => company.id === selectedMainId)

            if (mergeResult?.audit) {
                dispatch(appendMergeAuditLogEntry(mergeResult.audit))
            } else {
                dispatch(
                    appendMergeAuditLogEntry({
                        mainCompanyId: selectedMainId,
                        mainCompanyName: mainCompany?.name,
                        mergedCompanyIds: companyIdsToMerge,
                        mergedCompanyNames: companyIdsToMerge.map(
                            (id) =>
                                fullDetailsMap[id]?.name ||
                                allCompanies.find((company) => company.id === id)?.name ||
                                `#${id}`
                        ),
                    })
                )
            }

            await dispatch(CompaniesAction.fetchMergeAuditLog())

            toast.success(t('Companies merged successfully'))
            dispatch(closeModal())
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error('Merge failed', e)
            toast.error(t('Failed to merge companies'))
        } finally {
            setIsMerging(false)
        }
    }

    if (loadingDetails) {
        return (
            <UiFormModalShell
                title={t('Merge Companies')}
                className="MergeCompaniesModal"
            >
                <div className="p-10 flex justify-center text-gray-500">
                    {t('Loading company details...')}
                </div>
            </UiFormModalShell>
        )
    }

    return (
        <UiFormModalShell
            title={t('Merge Companies')}
            className="MergeCompaniesModal"
        >
            <div className="flex flex-col h-full max-h-[90vh]">
                {/* Search & Intro */}
                <div className="MergeCompaniesModalSearch px-6 pt-6 flex flex-col gap-4">
                    <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/10 rounded-xl border border-primary-100 dark:border-primary-800/30">
                        <DocumentDuplicateIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
                        <p className="text-sm text-primary-900 dark:text-primary-100/80 leading-relaxed">
                            {t(
                                'Select all companies you want to merge together, then pick one as the Main Record. All data will be moved into the selected main company.'
                            )}
                        </p>
                    </div>

                    <MergeAuditLogPanel />

                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) =>
                                    e.key === 'Enter' && handleSearch()
                                }
                                placeholder={t('Search by company name or CIN')}
                                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl focus:border-2 focus:border-primary-500 transition-all outline-none"
                            />
                        </div>
                        <UiButton
                            style={UiButtonStyle.Secondary}
                            loading={isSearching}
                            onClick={handleSearch}
                                className="min-w-30"
                        >
                            {t('Search')}
                        </UiButton>
                        <UiButton
                            style={UiButtonStyle.LIGHT}
                            onClick={handleReset}
                                className="min-w-30"
                        >
                            {t('Reset')}
                        </UiButton>
                    </div>
                    <div className="px-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t(
                                'You can search numerous times to find all companies you want. Newly found companies will appear at the top.'
                            )}
                        </p>
                    </div>

                    <MergeCompaniesSearchTagsList
                        tags={searchTags}
                        onRemoveTag={handleRemoveTag}
                    />
                </div>

                {/* Cards Container */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <MergeCompaniesCardsList
                        companies={allCompanies}
                        fullDetailsMap={fullDetailsMap}
                        selectedForMergeIds={selectedForMergeIds}
                        selectedMainId={selectedMainId}
                        expandedRows={expandedRows}
                        onToggleMergeSelection={toggleMergeSelection}
                        onSelectMainId={setSelectedMainId}
                        onToggleRow={toggleRow}
                        duplicateEmails={duplicateContactEmails}
                        translate={t}
                    />
                </div>

                {/* Footer Actions */}
                <div className="p-6 bg-gray-50 dark:bg-gray-900/50 border-t dark:border-gray-800 flex justify-end gap-3 rounded-b-2xl">
                    <div className="mr-auto flex items-center text-sm text-gray-500">
                        {t('Selected for merge')}:{' '}
                        <span className="font-bold ml-1 text-primary-600">
                            {selectedForMergeIds.size}
                        </span>
                    </div>
                    <UiButton
                        style={UiButtonStyle.Secondary}
                        onClick={() => dispatch(closeModal())}
                        size={UiButtonSize.Normal}
                    >
                        {t('Cancel')}
                    </UiButton>
                    <UiButton
                        style={UiButtonStyle.Primary}
                        disabled={
                            selectedForMergeIds.size < 2 ||
                            !selectedMainId ||
                            isMerging
                        }
                        onClick={handleMerge}
                        loading={isMerging}
                        size={UiButtonSize.Normal}
                        className="min-w-40"
                    >
                        {isMerging ? t('Merging...') : t('Confirm Merge')}
                    </UiButton>
                </div>
            </div>
        </UiFormModalShell>
    )
}

export default MergeCompaniesModal
